// @flow
import React, {useState, useEffect} from 'react';
import {
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  makeStyles,
  Snackbar,
  Typography,
} from '@material-ui/core';
import CustomTextField from '../../../../../elements/CustomTextField/CustomTextField';
import CustomButton from '../../../../../elements/CustomButton/CustomButton';
import {colors} from '../../../../../assets/styles/colors';
import {useForm} from 'react-hook-form';
import Alert from '@material-ui/lab/Alert';
import {Link, useHistory, useParams} from 'react-router-dom';
import CustomExitPrompt from '../../../../../elements/CustomExitPrompt/CustomExitPrompt';
import {ArrowBack, DeleteOutlined} from '@material-ui/icons';
import DeleteConfirmationModal from '../../../../products/components/DeleteConfirmationModal/DeleteConfirmationModal';

import queryString from 'query-string';
import {
  useCRUDFlatRate,
  useDataFlatRate,
  useGetCurrencyCountry,
  useResetState,
  useGetShippingMethodActive,
  useUpdateShippingMethodActive,
} from '../utils';
import {customErrorMessage} from '../../logistics/utils/customErrorMessage';
import ActivateShippingModal from '../components/ActivateShippingModal';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    border: '1px solid #dbdbe0',
    alignItems: 'flex-start',
    [theme.breakpoints.down('xs')]: {
      alignItems: 'stretch',
    },
    borderRadius: '12px',
  },
  subRouteHeader: {
    display: 'flex',
    alignItems: 'center',
    margin: theme.spacing(2, 0),
    width: '100%',
    paddingInline: theme.spacing(2),
  },
  subRouteTitle: {
    marginLeft: theme.spacing(1),
    fontSize: '1rem',
    fontWeight: 600,
  },
  divider: {
    background: '#e8e8ed',
    width: '100%',
  },
  checkboxLabel: {
    color: colors.grey700,
    fontSize: '0.875rem',
    margin: theme.spacing(3, 0),
  },
  checkboxOption: {
    color: colors.grey900,
    fontSize: '0.875rem',
    margin: theme.spacing(1, 0),
  },
  row: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
  },
  textField: {
    '&:not(:first-child)': {
      marginLeft: theme.spacing(3),
    },
  },
  textInput: {
    borderRadius: '8px',
    color: colors.grey800,
  },
  textInputLabel: {
    color: colors.grey600,
  },
  greyText: {
    color: colors.grey600,
    fontSize: '0.75rem',
  },
  messageSpace: {
    margin: theme.spacing(2, 0),
    alignSelf: 'stretch',
    display: 'flex',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
    },
  },
  messageField: {
    flexGrow: 1,
    marginBottom: theme.spacing(1),
  },
  messageTips: {
    margin: theme.spacing(0, 3),
    [theme.breakpoints.down('md')]: {
      margin: 'initial',
    },
  },
  bold: {
    fontWeight: 600,
  },
  saveButton: {
    marginBottom: theme.spacing(3),
    [theme.breakpoints.up('sm')]: {
      width: 200,
    },
    [theme.breakpoints.down('xs')]: {
      zIndex: 1,
      position: 'fixed',
      left: 0,
      bottom: 64,
      margin: theme.spacing(2),
      height: 48,
      width: `calc(100% - ${theme.spacing(4)})`,
      fontSize: '1.125rem',
    },
  },
  deleteButton: {
    marginBottom: theme.spacing(3),
    color: colors.error,
  },
  alert: {
    width: '100%',
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down('xs')]: {
      marginBottom: theme.spacing(2),
    },
  },
  form: {
    paddingInline: theme.spacing(3),
    width: '100%',
  },
}));

export default function LogisticsShippingManualEdit() {
  const classes = useStyles();
  const history = useHistory();
  const query = queryString.parse(window.location.search);

  const {flatRateId} = useParams();
  const [showModalActive, setShowModalActive] = useState(false);
  const {flatRate, fetchFlatRate} = useDataFlatRate();
  const currency = useGetCurrencyCountry();
  const {resetState} = useResetState();
  const {updateShippingMethodActive} = useUpdateShippingMethodActive();
  const {
    messageError,
    isSaving,
    savingFlatRateSuccess,
    savingFlatRateError,
    createFlatRate,
    updateFlatRate,
    removeFlatRate,
  } = useCRUDFlatRate();
  const [alertOpenError, setAlertOpenError] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [optionSave, setOptionSave] = useState('');
  const {register, handleSubmit, errors, formState, getValues} = useForm();
  const {isDirty} = formState;

  const [promptOpen, setPromptOpen] = useState(false);
  const shippingMethod = useGetShippingMethodActive();
  const handlePromptOpen = () => setPromptOpen(true);
  const handlePromptClose = () => setPromptOpen(false);
  const handleDeleteClick = () => {
    if (flatRate) removeFlatRate(flatRate.id);
  };

  const handleToggleEnabled = event => {
    if (event && flatRate) {
      setEnabled(event.target.checked);
      const flatRateRequest = {
        flatRateId: flatRate.id,
        flatRate: {
          enabled: event.target.checked,
        },
      };
      updateFlatRate(flatRateRequest);
    } else {
      setEnabled(event.target.checked);
    }
  };

  const handleValidation = () => {
    if (
      shippingMethod.shippingMethodActive.storeShippingMethodSettings
        .storeShippingMethodType !== 'FLAT_RATE'
    ) {
      setShowModalActive(true);
      return false;
    }
    return true;
  };

  const handleBeforeSubmit = () => {
    if (!handleValidation()) {
      return;
    }

    handleFormSubmit('save');
  };

  const handleFormSubmit = (optionSave: string) => {
    const data = getValues();
    const flatRateId = flatRate ? flatRate.id : undefined;
    const flatRatePrice = flatRate
      ? flatRate.shippingRates[0].price
      : undefined;

    const flatRateDisplayName = flatRate ? flatRate.shippingRates[0].name : '';
    const flatRateDescription = flatRate
      ? flatRate.shippingRates[0].description
      : '';

    const flatRateRequest = {
      flatRateId,
      flatRate: {
        enabled,
        price: data.price ?? flatRatePrice,
        displayName: data.displayName ?? flatRateDisplayName,
        description: data.description ?? flatRateDescription,
      },
    };
    if (optionSave === 'saveAndActivate') {
      setOptionSave('saveAndActivate');
    }

    if (flatRate) updateFlatRate(flatRateRequest);
    else {
      createFlatRate(flatRateRequest);
    }
  };

  useEffect(() => {
    if (flatRateId && flatRateId !== 'new') fetchFlatRate(flatRateId);
  }, [fetchFlatRate, flatRateId]);

  useEffect(() => {
    if (flatRate) {
      if (flatRate.enabled) setEnabled(true);
      else setEnabled(false);
    }
  }, [flatRate]);

  useEffect(() => {
    if (savingFlatRateSuccess) {
      if (query.fromOnboarding) {
        history.push('/home');
      } else {
        if (optionSave === 'saveAndActivate') {
          updateShippingMethodActive({
            shippingMethodType: 'FLAT_RATE',
          });
        }
        history.push('/configure/logistics/shipping/manual');
      }
    }
  }, [
    history,
    optionSave,
    query,
    savingFlatRateSuccess,
    updateShippingMethodActive,
  ]);

  useEffect(() => {
    if (savingFlatRateError) {
      setAlertOpenError(true);
      if (messageError === 'You must have at least one flat rate activated') {
        setEnabled(true);
      }
    }
  }, [messageError, savingFlatRateError]);

  useEffect(() => {
    return () => {
      resetState();
    };
  }, [resetState]);

  return (
    <div className={classes.root}>
      <CustomExitPrompt when={isDirty && !savingFlatRateSuccess} />
      <Snackbar
        open={alertOpenError}
        autoHideDuration={4000}
        onClose={() => setAlertOpenError(false)}
      >
        <Alert severity="error" onClose={() => setAlertOpenError(false)}>
          {customErrorMessage(messageError)}
        </Alert>
      </Snackbar>

      <div className={classes.subRouteHeader}>
        <IconButton
          variant="contained"
          size="small"
          component={Link}
          to="/configure/logistics/shipping/manual"
        >
          <ArrowBack />
        </IconButton>
        <Typography className={classes.subRouteTitle}>
          {flatRate ? flatRate.shippingRates[0].name : 'Nuevo método'}
        </Typography>
      </div>
      <Divider className={classes.divider} />
      <div className={classes.form}>
        <FormControlLabel
          classes={{label: classes.checkboxLabel}}
          control={
            <Checkbox
              checked={enabled}
              onChange={handleToggleEnabled}
              color="primary"
              disabled={isSaving}
            />
          }
          label="Activar"
        />
        {(flatRate || flatRateId === 'new') && (
          <form onSubmit={handleSubmit(handleBeforeSubmit)}>
            <Grid spacing={2} container>
              <Grid item xs={12} sm={6}>
                <CustomTextField
                  className={classes.textField}
                  label="Nombre (Ej. Lima Norte)"
                  InputProps={{
                    className: classes.textInput,
                  }}
                  InputLabelProps={{className: classes.textInputLabel}}
                  name="displayName"
                  defaultValue={flatRate ? flatRate.shippingRates[0].name : ''}
                  error={!!errors.displayName}
                  helperText={
                    errors.displayName ? errors.displayName.message : ''
                  }
                  inputRef={register({
                    required: 'Campo obligatorio',
                  })}
                  type="text"
                  disabled={isSaving}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomTextField
                  className={classes.textField}
                  fullWidth
                  label="Costo de envío"
                  InputProps={{
                    className: classes.textInput,
                    startAdornment: (
                      <InputAdornment position="start">
                        {currency ? currency.symbol : ''}
                      </InputAdornment>
                    ),
                  }}
                  InputLabelProps={{className: classes.textInputLabel}}
                  name="price"
                  defaultValue={flatRate ? flatRate.shippingRates[0].price : ''}
                  error={!!errors.price}
                  helperText={errors.price ? errors.price.message : ''}
                  inputRef={register({
                    required: 'Campo obligatorio',
                  })}
                  type="number"
                  inputProps={{step: '.01'}}
                  disabled={isSaving}
                />
              </Grid>
            </Grid>
            <div className={classes.messageSpace}>
              <CustomTextField
                multiline
                rows={5}
                rowsMax={18}
                label="Mensaje"
                className={`${classes.textField} ${classes.messageField}`}
                InputProps={{
                  className: classes.textInput,
                }}
                InputLabelProps={{className: classes.textInputLabel}}
                name="description"
                error={!!errors.description}
                helperText={errors.description ? errors.description : ''}
                defaultValue={
                  flatRate && flatRate.shippingRates[0].description
                    ? flatRate.shippingRates[0].description
                    : ``
                }
                type="text"
                inputRef={register}
                disabled={isSaving}
              />
              <div className={classes.messageTips}>
                <Typography className={classes.greyText} paragraph>
                  Es importante incluir información como:
                </Typography>
                <Typography className={`${classes.greyText} ${classes.bold}`}>
                  Días de despacho
                </Typography>
                <Typography className={classes.greyText} paragraph>
                  Despachos los martes y jueves
                </Typography>
                <Typography className={classes.greyText} paragraph>
                  Distritos de Nombre 1, Nombre 2, Nombre 3, Nombre 4.
                </Typography>
              </div>
            </div>
            <br />
            <div className={classes.row}>
              <CustomButton
                variant="contained"
                color="primary"
                className={classes.saveButton}
                type="submit"
                disabled={isSaving}
                // onClick={handleSubmit(handleFormSubmit)}
              >
                Guardar cambios
              </CustomButton>
              <div />
              {flatRate && (
                <CustomButton
                  className={classes.deleteButton}
                  endIcon={<DeleteOutlined />}
                  onClick={handlePromptOpen}
                >
                  Eliminar método
                </CustomButton>
              )}
            </div>
            {flatRate && (
              <DeleteConfirmationModal
                title="¿Seguro que quieres eliminar este método?"
                content="Si lo eliminas no lo podrás recuperar."
                open={promptOpen}
                handleClose={handlePromptClose}
                handleDeleteClick={handleDeleteClick}
              />
            )}
          </form>
        )}
      </div>

      <ActivateShippingModal
        open={showModalActive}
        handleClose={() => {
          setShowModalActive(false);
        }}
        typeRateSelected="FLAT_RATE"
        handleFormSubmit={handleFormSubmit}
        isSaving={isSaving}
      />
    </div>
  );
}
